import React, { useEffect, useState } from "react";
import { BASE_URL } from "../../Config/api.config";
import useLocalStorage from "../../Hooks/useLocalStorage";
import AllWorkersData from "./AllWorkersData";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Button,
  Box,
  Paper,
  IconButton,
  InputAdornment,
  Chip,
  Stack,
  Pagination,
  Typography,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  DateRange as DateRangeIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const WorkersPanel = () => {
  const [adminData] = useLocalStorage("adminData", "");
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Filters state
  const [filters, setFilters] = useState({
    search: "",
    active: "",
    validated: "",
    country_code: "",
    date_from: null,
    date_to: null,
    sort_by: "date_created",
    sort_order: "DESC",
    page: 1,
    limit: 20,
  });

  // Country codes (you can fetch this from API if available)
  const [countries, setCountries] = useState([
    { code: "US", name: "United States" },
    { code: "GB", name: "United Kingdom" },
    { code: "CA", name: "Canada" },
    { code: "AU", name: "Australia" },
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" },
    { code: "ES", name: "Spain" },
    { code: "IT", name: "Italy" },
    { code: "JP", name: "Japan" },
    { code: "IN", name: "India" },
  ]);

  const sortOptions = [
    { value: "date_created", label: "Registration Date" },
    { value: "first_name", label: "First Name" },
    { value: "last_name", label: "Last Name" },
    { value: "email", label: "Email" },
    { value: "active", label: "Active Status" },
    { value: "validated", label: "Validation Status" },
    { value: "country_code_name", label: "Country" },
  ];

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "1", label: "Active" },
    { value: "0", label: "Inactive" },
  ];

  const validationOptions = [
    { value: "", label: "All Validation" },
    { value: "1", label: "Validated" },
    { value: "0", label: "Not Validated" },
  ];

  const limitOptions = [10, 20, 50, 100];

  const buildQueryString = (filters) => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== "" && value !== undefined) {
        if (key === "date_from" || key === "date_to") {
          if (value) {
            params.append(key, value.toISOString().split("T")[0]);
          }
        } else {
          params.append(key, value.toString());
        }
      }
    });

    return params.toString();
  };

  useEffect(() => {
    console.log("workers", workers);
  }, [workers]);
  const getAllWorkers = (currentFilters = filters) => {
    setLoading(true);

    const queryString = buildQueryString(currentFilters);
    const url = `${BASE_URL}adm.workers${queryString ? `?${queryString}` : ""}`;

    fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${adminData.uid}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("data", data);
        if (data.status === "ok") {
          setWorkers(data.result.data || []);
          setTotalItems(data.result.pagination?.total_items || 0);
          setTotalPages(data.result.pagination?.total_pages || 1);
        } else {
          console.error("API Error:", data.message);
          setWorkers([]);
        }
      })
      .catch((error) => {
        console.error("Fetch Error:", error);
        setWorkers([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Fetch countries from API (if available)
  useEffect(() => {
    // Uncomment if you have an endpoint for countries
    /*
    // fetch(`${BASE_URL}countries`, {
    //   headers: { Authorization: `Bearer ${adminData.uid}` },
    // })
    //   .then(res => res.json())
    //   .then(data => {
    //     if (data.status === "success") {
    //       setCountries(data.data);
    //     }
    //   })
    //   .catch(console.log);
    // */
  }, [adminData]);

  useEffect(() => {
    getAllWorkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit, filters.sort_by, filters.sort_order]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      // Reset to page 1 when changing filters (except page and limit)
      ...(key !== "page" && key !== "limit" ? { page: 1 } : {}),
    }));
  };

  const handleSearch = () => {
    getAllWorkers({ ...filters, page: 1 });
  };

  const handleResetFilters = () => {
    const resetFilters = {
      search: "",
      active: "",
      validated: "",
      country_code: "",
      date_from: null,
      date_to: null,
      sort_by: "date_created",
      sort_order: "DESC",
      page: 1,
      limit: 20,
    };
    setFilters(resetFilters);
    getAllWorkers(resetFilters);
  };

  const handlePageChange = (event, value) => {
    handleFilterChange("page", value);
  };

  const activeFilterCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.active !== "") count++;
    if (filters.validated !== "") count++;
    if (filters.country_code) count++;
    if (filters.date_from) count++;
    if (filters.date_to) count++;
    return count;
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="py-6 px-6 w-full">
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ fontWeight: "bold" }}
          >
            Workers Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and filter worker accounts. Total: {totalItems} workers
          </Typography>
        </Box>

        {/* Filters Panel */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
            {activeFilterCount() > 0 && (
              <Chip
                label={`${activeFilterCount()} active`}
                size="small"
                color="primary"
                sx={{ ml: 2 }}
              />
            )}
          </Box>

          <Grid container spacing={2}>
            {/* Search */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search by name or email"
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: filters.search && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => handleFilterChange("search", "")}
                      >
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>

            {/* Status Filters */}
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Active Status</InputLabel>
                <Select
                  value={filters.active}
                  label="Active Status"
                  onChange={(e) => handleFilterChange("active", e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Validation</InputLabel>
                <Select
                  value={filters.validated}
                  label="Validation"
                  onChange={(e) =>
                    handleFilterChange("validated", e.target.value)
                  }
                >
                  {validationOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Country Filter */}
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Country</InputLabel>
                <Select
                  value={filters.country_code}
                  label="Country"
                  onChange={(e) =>
                    handleFilterChange("country_code", e.target.value)
                  }
                >
                  <MenuItem value="">All Countries</MenuItem>
                  {countries.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Date Range Filters */}
            <Grid item xs={6} md={3}>
              <DatePicker
                label="From Date"
                value={filters.date_from}
                onChange={(newValue) =>
                  handleFilterChange("date_from", newValue)
                }
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <DateRangeIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <DatePicker
                label="To Date"
                value={filters.date_to}
                onChange={(newValue) => handleFilterChange("date_to", newValue)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
              />
            </Grid>

            {/* Sort Options */}
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={filters.sort_by}
                  label="Sort By"
                  onChange={(e) =>
                    handleFilterChange("sort_by", e.target.value)
                  }
                >
                  {sortOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Order</InputLabel>
                <Select
                  value={filters.sort_order}
                  label="Order"
                  onChange={(e) =>
                    handleFilterChange("sort_order", e.target.value)
                  }
                >
                  <MenuItem value="DESC">Descending</MenuItem>
                  <MenuItem value="ASC">Ascending</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={filters.limit}
                  label="Items per page"
                  onChange={(e) => handleFilterChange("limit", e.target.value)}
                >
                  {limitOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  startIcon={<SearchIcon />}
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Search"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleResetFilters}
                  startIcon={<RefreshIcon />}
                >
                  Reset
                </Button>
              </Stack>
            </Grid>
          </Grid>

          {/* Active Filters Chips */}
          {(filters.search ||
            filters.active !== "" ||
            filters.validated !== "" ||
            filters.country_code ||
            filters.date_from ||
            filters.date_to) && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mr: 1 }}
              >
                Active filters:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {filters.search && (
                  <Chip
                    label={`Search: "${filters.search}"`}
                    size="small"
                    onDelete={() => handleFilterChange("search", "")}
                  />
                )}
                {filters.active !== "" && (
                  <Chip
                    label={`Active: ${filters.active === "1" ? "Yes" : "No"}`}
                    size="small"
                    onDelete={() => handleFilterChange("active", "")}
                  />
                )}
                {filters.validated !== "" && (
                  <Chip
                    label={`Validated: ${
                      filters.validated === "1" ? "Yes" : "No"
                    }`}
                    size="small"
                    onDelete={() => handleFilterChange("validated", "")}
                  />
                )}
                {filters.country_code && (
                  <Chip
                    label={`Country: ${
                      countries.find((c) => c.code === filters.country_code)
                        ?.name || filters.country_code
                    }`}
                    size="small"
                    onDelete={() => handleFilterChange("country_code", "")}
                  />
                )}
                {filters.date_from && (
                  <Chip
                    label={`From: ${formatDate(filters.date_from)}`}
                    size="small"
                    onDelete={() => handleFilterChange("date_from", null)}
                  />
                )}
                {filters.date_to && (
                  <Chip
                    label={`To: ${formatDate(filters.date_to)}`}
                    size="small"
                    onDelete={() => handleFilterChange("date_to", null)}
                  />
                )}
              </Stack>
            </Box>
          )}
        </Paper>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2">
                Page {filters.page} of {totalPages} • Showing {workers.length}{" "}
                of {totalItems} workers
              </Typography>
              <Pagination
                count={totalPages}
                page={filters.page}
                onChange={handlePageChange}
                color="primary"
                size="medium"
                showFirstButton
                showLastButton
              />
            </Box>
          </Paper>
        )}

        {/* Workers Table */}
        <AllWorkersData
          workers={workers}
          loading={loading}
          refreshWorkers={getAllWorkers}
        />

        {/* Bottom Pagination */}
        {totalPages > 1 && (
          <Paper elevation={1} sx={{ p: 2, mt: 3, borderRadius: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Pagination
                count={totalPages}
                page={filters.page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          </Paper>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default WorkersPanel;
